import { type FC, useRef } from "react";

import { filterByCategory } from "@/shared/constants/estateCategories";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItemActions } from "@/shared/hooks/useEstateItemActions";
import { supabase } from "@/shared/lib/supabase";
import { uploadSupportingDocument } from "@/shared/utils/uploadSupportingDocument";
import { ReviewScreenLayout } from "@/pages/postloss/modules/components/ReviewScreenLayout";
import { useReviewRedirect } from "@/pages/postloss/modules/hooks/useReviewRedirect";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

import {
  PhysicalAssetForm,
  buildPhysicalAssetSubtitle,
  parsePhysicalAssetToForm,
  physicalAssetToItemFields,
  validatePhysicalAssetFields,
  type PhysicalAssetFormValues,
} from "./PhysicalAssetForm";

export const PhysicalAssetsReview: FC<FlowCustomComponentProps> = ({
  onComplete,
  goToScreen,
  answers,
  setAnswer,
}) => {
  const { person } = useAppContext();
  const estateId = person?.estate_id;
  const { assets, isLoading, isFetching, updateItem, isUpdatingItem, deleteItem, itemActions } =
    useEstateItemActions(estateId);

  const dialogRef = useRef<HTMLDivElement>(null);
  const items = filterByCategory(assets, "Valuables");
  const firstName = person?.first_name ?? "their";

  const { itemsEverFound, alreadyRedirected } = useReviewRedirect({
    items,
    isLoading,
    isFetching,
    answers,
    setAnswer,
    goToScreen,
    reviewKey: "physical_assets_review",
    addKey: "add_physical_asset",
    fallbackScreen: "has_physical_assets",
    fallbackAnswers: {
      ...answers,
      physical_assets_review: "none",
      add_physical_asset: "",
    },
  });

  return (
    <ReviewScreenLayout<PhysicalAssetFormValues>
      items={items}
      isLoading={isLoading}
      isFetching={isFetching}
      isRedirecting={items.length === 0 && !itemsEverFound && !alreadyRedirected}
      screenTitle={`Review ${firstName}'s valuables`}
      screenSubtitle="Review the details and add any others we might have missed."
      itemLabel="personal property"
      listTitle="Valuable personal property"
      editDialogTitle="Edit personal property"
      editDialogRef={dialogRef}
      deleteDialogTitle="Delete Personal Property"
      isUpdatingItem={isUpdatingItem}
      deleteItem={deleteItem}
      itemActions={itemActions}
      onAddAnother={() => {
        goToScreen("add_physical_asset", {
          ...answers,
          has_physical_assets: "yes",
          add_physical_asset: "adding",
        });
      }}
      onContinue={() => {
        setAnswer("add_physical_asset", "done");
        onComplete();
      }}
      parseItemToForm={(item) => ({
        ...parsePhysicalAssetToForm(item.item_type ?? "", item.balance, item.notes),
        description: item.item_name || "",
      })}
      renderSubtitle={(item) =>
        buildPhysicalAssetSubtitle(item.item_type, item.balance, item.notes)
      }
      renderEditForm={({ form, onChange, existingFileName, onFileRemove }) => (
        <PhysicalAssetForm
          form={form}
          onChange={onChange}
          existingFileName={existingFileName}
          onFileRemoved={onFileRemove}
        />
      )}
      onSaveEdit={async ({ id, form, fileRemoved }) => {
        if (!estateId) return;

        const validationError = Object.values(validatePhysicalAssetFields(form))[0];
        if (validationError) throw new Error(validationError);

        let sourceDocumentId: number | undefined;

        if (form.file) {
          const {
            data: { session },
          } = await supabase.auth.getSession();
          if (!session) return;
          sourceDocumentId = await uploadSupportingDocument(
            form.file,
            estateId,
            session.access_token,
            "other",
          );
        }

        const sourceDocUpdate = sourceDocumentId
          ? { source_document_id: sourceDocumentId }
          : fileRemoved
            ? { source_document_id: null }
            : {};

        const fields = physicalAssetToItemFields(form);
        await updateItem(id, {
          item_name: form.description.trim(),
          item_type: fields.item_type,
          balance: fields.balance,
          notes: fields.notes,
          ...sourceDocUpdate,
        });
      }}
    />
  );
};
